import { Breakpoints } from '@angular/cdk/layout';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { BnNgIdleService } from 'bn-ng-idle';

import { DialogComponent } from './core/components/dialog/dialog.component';
import { NavigationComponent } from './core/components/navigation/navigation.component';
import { CoreModule } from './core/core.module';
import { BreakpointHelpers } from './models/BreakpointHelpers';
import { DialogData } from './models/DialogData';
import { DialogOptions } from './models/Enums/DialogOptions';
import { DialogResults } from './models/Enums/DialogResults';
import { AuthenticationService } from './services/authentication.service';
import { BreakpointService } from './services/breakpoint.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'stine-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [RouterOutlet, NavigationComponent, CoreModule],
  standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
  public currentYear = new Date().getFullYear();
  public title = 'Sales';
  private currentScreenSize: string;
  private timeLeft: number;
  private interval: ReturnType<typeof setInterval> | null = null;
  private isIdleDialogOpen = false;
  private dialogData: DialogData = {
    dialogOption: DialogOptions.StayLogout,
    title: 'Are you still there?',
    description: `Your session has been inactive. You will be automatically logged out in ${environment.idleTimeLeftInSec} seconds.`,
    subDescription: 'Click "Stay" to continue using the application.',
  };

  public get isLoggedIn(): boolean {
    const user = this.authService.getCurrentUser();
    return user && this.authService.isUserAuthenticated();
  }
  private destroyRef = inject(DestroyRef);
  private breakpointService = inject(BreakpointService);
  private authService = inject(AuthenticationService);
  private msalService = inject(MsalService);
  private bnIdle = inject(BnNgIdleService);
  private dialog = inject(MatDialog);

  constructor() {
    this.startWatchingUserIdle();
  }

  ngOnInit(): void {
    this.authService.waitForMsalInitialization().subscribe({
      next: () => {
        this.msalService.handleRedirectObservable().subscribe({
          next: (result) => {
            if (result) {
              console.log('MSAL redirect handled successfully', result);
            }
          },
          error: (error) => {
            console.error('MSAL redirect handling error:', error);
          },
        });
      },
      error: (error) => {
        console.error('MSAL initialization error:', error);
      },
    });

    this.breakpointService.screenSize$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((screenSize) => {
        this.currentScreenSize = screenSize;
      });
  }

  public get xSmallScreenSize(): boolean {
    return (
      this.currentScreenSize === BreakpointHelpers.displayNameMap.get(Breakpoints.Medium) ||
      this.currentScreenSize === BreakpointHelpers.displayNameMap.get(Breakpoints.Small) ||
      this.currentScreenSize === BreakpointHelpers.displayNameMap.get(Breakpoints.XSmall)
    );
  }

  ngOnDestroy(): void {
    this.clearTimeoutInterval();
    this.bnIdle.stopTimer();
  }

  public startWatchingUserIdle(): void {
    this.bnIdle.startWatching(environment.idleTimeoutInSec).subscribe((isIdle) => {
      if (this.authService.isUserAuthenticated()) {
        this.timeLeft = environment.idleTimeLeftInSec;
        if (isIdle && !this.isIdleDialogOpen) {
          this.isIdleDialogOpen = true;
          this.interval = setInterval(() => {
            this.checkTimer();
          }, 1000);

          const dialogRef = this.dialog.open(DialogComponent, {
            data: this.dialogData,
            disableClose: true,
          });

          dialogRef.afterClosed().subscribe((result) => {
            this.isIdleDialogOpen = false;
            if (result === DialogResults.Stay) {
              // User confirmed they're still there - reset everything
              this.dialogData.description = `Your session has been inactive. You will be automatically logged out in ${environment.idleTimeLeftInSec} seconds.`;
              this.clearTimeoutInterval();
              this.bnIdle.resetTimer();
            } else {
              // User chose to logout or dialog was closed
              this.bnIdle.stopTimer();
              this.clearTimeoutInterval();
              this.dialog.closeAll();
              this.authService.logout();
            }
          });
        }
      }
    });
  }

  private checkTimer(): void {
    this.timeLeft--;
    this.dialogData.description = `Your session has been inactive. You will be automatically logged out in ${this.timeLeft} seconds.`;

    if (this.timeLeft <= 0) {
      this.bnIdle.stopTimer();
      this.dialog.closeAll();
      this.authService.logout();
      this.clearTimeoutInterval();
    }
  }

  private clearTimeoutInterval(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}
